/**
 *
 * EditPasswordLayout
 *
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Button,
  PasswordTextfield,
  stylize,
} from "@envestnet/envreact-component-library";
import classnames from "classnames";
import withIntl from "utils/withIntl";

import PasswordRequirements from "components/PasswordRequirements";
import { getPasswordPolicyStatements } from "scenes/PasswordSetupPage/getPasswordPolicies";
import { PasswordPolicy } from "types/PasswordPolicy";
import { UserDetails, UserInfo } from "types/UserInfo";
import {
  emptyLengthCheck,
  includeFirstNameLastNameCheck,
  includeNumberCheck,
  lowerCaseCheck,
  minimumLengthCheck,
  specialCharCheck,
  upperCaseCheck,
} from "utils/passwordValidator";
import messages from "./messages";
import styles, { Styles } from "./styles";
import { PasswordRules } from "./types";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;
  loadingPasswordPolicies: boolean;
  passwordPolicies: PasswordPolicy;
  loadPasswordPolicies: () => void;
  goToSecuritySettings: () => void;
  goToDashboard: () => void;
  submitChangePassword: ({
    oldPassword,
    newPassword,
  }: {
    oldPassword: string;
    newPassword: string;
  }) => void;
  userName: string;
  userInfo: UserInfo;
  userDetails: UserDetails;
  isOldPasswordIncorrect: boolean;
  resetApiPasswordErrors: () => void;
  logout: () => void;
  hasUpdatedTnCAccepted: boolean;
  tncForCidpUser: string | null;
  getTnCForCidpUser: () => void;
  setUserAcceptedNewTnC: () => void;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
}

export interface EditPasswordLayoutProps extends DefaultProps {}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & EditPasswordLayoutProps;

const EditPasswordLayout = (props: Props) => {
  const {
    classes,
    formatMessage,
    goToSecuritySettings,
    goToDashboard,
    passwordPolicies,
    loadingPasswordPolicies,
    loadPasswordPolicies,
    submitChangePassword,
    userName,
    userInfo,
    userDetails,
    resetApiPasswordErrors,
    isOldPasswordIncorrect,
    getTnCForCidpUser,
  } = props;

  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [password, setNewPassword] = useState<string>("");
  const [newPasswordConfirmed, setNewPasswordConfirmed] = useState<string>("");
  const [newPasswordValidationErr, setNewPasswordValidationErr] = useState<
    string[] | null
  >();
  const [
    newPasswordConfirmedValidationErr,
    setNewPasswordConfirmedValidationErr,
  ] = useState<string | null | string[]>();
  const [currentPasswordValidationErr, setcurrentPasswordValidationErr] =
    useState<string | null>();

  useEffect(() => {
    if (userInfo.cidpId !== "" && userDetails.cidpId !== "") {
      if (!userDetails.directLoginEnabled) {
        goToDashboard();
      } else {
        loadPasswordPolicies();
      }
    }
  }, [userDetails, userInfo]);

  useEffect(() => {
    if ((userInfo as UserInfo).email !== "") {
      getTnCForCidpUser();
    }
  }, [userInfo]);

  useEffect(() => {
    if (isOldPasswordIncorrect) {
      setcurrentPasswordValidationErr(
        formatMessage(messages.tryAgainErrorMessage)
      );
    }
  }, [isOldPasswordIncorrect]);

  const passwordRules: PasswordRules | any = useMemo(
    () => getPasswordPolicyStatements(passwordPolicies, formatMessage),
    [passwordPolicies]
  );

  const passwordRuleCheck = useMemo(() => {
    if (passwordRules && !loadingPasswordPolicies) {
      return passwordRules && passwordRules?.passwordRequiredCheck;
    }
    return null;
  }, [passwordRules, loadingPasswordPolicies]);

  const getUserNameCheck = (validator, newValue) =>
    userName && validator?.excludeUsername
      ? includeFirstNameLastNameCheck(newValue, userName)
      : false;

  const getLowerCaseCheck = (validator, newValue) =>
    validator?.minLowerCase ? lowerCaseCheck(newValue) : false;

  const getUpperCaseCheck = (validator, newValue) =>
    validator?.minLowerCase ? upperCaseCheck(newValue) : false;

  const getSpecialCharCheck = (validator, newValue) =>
    validator?.minSymbol ? specialCharCheck(newValue) : false;

  const validateRequired = useCallback(
    (
      newValue: string,
      passwordRule: any,
      setErrFn: (validationErrMsg?: string[]) => any
    ) => {
      const validator = passwordRule;
      const errorLengthCheck = emptyLengthCheck(newValue);
      const userNameCheck = getUserNameCheck(validator, newValue);
      const lowerCheck = getLowerCaseCheck(validator, newValue);
      const upperCheck = getUpperCaseCheck(validator, newValue);
      const specialCheck = getSpecialCharCheck(validator, newValue);
      const numCheck = validator?.minNumber
        ? includeNumberCheck(newValue)
        : false;
      const minLengthCheck = validator?.minLength
        ? minimumLengthCheck(newValue, validator?.minLength)
        : false;
      const firstNameCheck = validator?.firstName
        ? includeFirstNameLastNameCheck(newValue, userInfo.given_name)
        : false;
      const lastNameCheck = validator?.lastName
        ? includeFirstNameLastNameCheck(newValue, userInfo.family_name)
        : false;
      const error: any = [];
      if (userNameCheck) {
        error.push(formatMessage(messages.errMsgUserName));
      }
      if (errorLengthCheck) {
        error.push(formatMessage(messages.errMsgRequiredField));
      }
      if (lowerCheck) {
        error.push(formatMessage(messages.errMsgLowerCase));
      }
      if (upperCheck) {
        error.push(formatMessage(messages.errMsgUpperCase));
      }
      if (specialCheck) {
        error.push(formatMessage(messages.errMsgSpecialChar));
      }
      if (numCheck) {
        error.push(formatMessage(messages.errMsgNumber));
      }
      if (minLengthCheck) {
        error.push(
          formatMessage(messages.errMsgMaxLength, {
            changedValue: validator?.minLength,
          })
        );
      }
      if (firstNameCheck) {
        error.push(formatMessage(messages.errMsgFirstName));
      }
      if (lastNameCheck) {
        error.push(formatMessage(messages.errMsgLastName));
      }
      setErrFn(error);
      return false;
    },
    [passwordRuleCheck]
  );

  const validateNewPasswordsEquality = useCallback(
    (
      newValue: string,
      expectedValue: string,
      setErrFn: (validationErrMsg) => void
    ) => {
      if (newValue !== expectedValue) {
        setErrFn(formatMessage(messages.errMsgUnconfirmedPassword));
        return true;
      }

      setErrFn(undefined);
      return false;
    },
    [formatMessage]
  );

  const allowSubmit = useMemo(() => {
    if (
      (!!newPasswordValidationErr && newPasswordValidationErr.length) ||
      (!!newPasswordConfirmedValidationErr &&
        newPasswordConfirmedValidationErr.length)
    ) {
      return false;
    }

    if (
      password.trim().length < 1 ||
      newPasswordConfirmed.trim().length < 1 ||
      currentPassword.trim().length < 1
    ) {
      return false;
    }

    if (password !== newPasswordConfirmed) {
      return false;
    }

    return true;
  }, [
    password,
    newPasswordConfirmed,
    newPasswordValidationErr,
    newPasswordConfirmedValidationErr,
  ]);

  const onChangeNewPassword = useCallback(
    (newValue: string) => {
      setNewPassword(newValue);
    },
    [newPasswordConfirmed, passwordRuleCheck]
  );

  const onblurNewPassword = useCallback(() => {
    validateRequired(password, passwordRuleCheck, setNewPasswordValidationErr);
  }, [newPasswordConfirmed, password]);

  const onChangeNewPasswordConfirmed = useCallback(
    (newValue: string) => {
      setNewPasswordConfirmed(newValue);
    },
    [password, passwordRuleCheck]
  );

  const onBlurNewPasswordConfirmed = useCallback(() => {
    validateNewPasswordsEquality(
      newPasswordConfirmed,
      password,
      setNewPasswordConfirmedValidationErr
    );
  }, [password, passwordRuleCheck, newPasswordConfirmed]);

  const onChangeCurrentPassword = useCallback(
    (newValue: string) => {
      setCurrentPassword(newValue);

      if (currentPasswordValidationErr) {
        setcurrentPasswordValidationErr(null);
      }

      if (emptyLengthCheck(newValue)) {
        setcurrentPasswordValidationErr(
          formatMessage(messages.errMsgRequiredField)
        );
      }
    },
    [currentPassword]
  );

  return (
    <div className={classes.mainSectionParent}>
      <div className={classes.content}>
        <div>
          <header className={classes.header}>
            <div className={classes.headerWrapper}>
              <span
                className={classnames(classes.heading, "header-font-family")}
              >
                {formatMessage(messages.updatePassword)}
              </span>
            </div>
          </header>
          <div className={classes.passwordWrapper}>
            <div className={classes.password}>
              <div
                className={classes.currentPassword}
                data-testid="current-password-text"
              >
                <PasswordTextfield
                  aria-required
                  className={classnames(
                    classes.textfield,
                    "header-font-family"
                  )}
                  label={formatMessage(messages.currentPassword)}
                  aria-label="current password text field"
                  onChangeText={onChangeCurrentPassword}
                  text={currentPassword}
                  errorMessage={currentPasswordValidationErr}
                />
              </div>
              <div data-testid="new-password-text">
                <PasswordTextfield
                  aria-required
                  className={classnames(
                    classes.textfield,
                    "header-font-family"
                  )}
                  label={formatMessage(messages.newPassword)}
                  aria-label="new password text field"
                  onChangeText={onChangeNewPassword}
                  onBlur={onblurNewPassword}
                  text={password}
                  errorMessage={newPasswordValidationErr}
                />
              </div>
              <div data-testid="confirm-new-password-text">
                <PasswordTextfield
                  aria-required
                  className={classnames(
                    classes.textfield,
                    "header-font-family"
                  )}
                  label={formatMessage(messages.confirmNewPassword)}
                  aria-label="confirm new password text field"
                  onChangeText={onChangeNewPasswordConfirmed}
                  onBlur={onBlurNewPasswordConfirmed}
                  text={newPasswordConfirmed}
                  errorMessage={newPasswordConfirmedValidationErr}
                />
              </div>
            </div>
            <PasswordRequirements
              passwordRules={passwordRules}
              loading={loadingPasswordPolicies}
            />
          </div>
        </div>
        <div className={classes.buttonContainer}>
          <Button
            buttonStyle="secondary"
            name={formatMessage(messages.cancel)}
            onClick={() => {
              resetApiPasswordErrors();
              goToSecuritySettings();
            }}
            className={classnames(classes.btn, "header-font-family")}
            data-testid="cancel-button"
          />
          <Button
            buttonStyle="primary"
            disabled={!allowSubmit}
            name={formatMessage(messages.save)}
            onClick={() =>
              submitChangePassword({
                oldPassword: currentPassword,
                newPassword: password,
              })
            }
            className={classnames(classes.btn, "header-font-family")}
            data-testid="submit-button"
          />
        </div>
      </div>
      <></>
    </div>
  );
};

EditPasswordLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(EditPasswordLayout)
);
